import React, { useState, useCallback, useMemo } from 'react';
import {
	CheckSquare,
	Calendar,
	Briefcase,
	Building2,
	User,
	Sparkles,
	FileText,
	Ticket,
	Folder,
	ClipboardList,
	ExternalLink,
	Trash2,
	Users
} from 'lucide-react';
import T from 'Components/Helpers/translate';
import AssistantResource from 'Resources/AssistantChat';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { openCrmEntity, canOpenCrmEntity, crmEntityLabelKey, orderProbabilityOf } from './openCrmEntity';
import { openCrmEntityDraft, canEditCrmEntityDraft } from './openCrmEntityDraft';
import FieldGrid, { withValidAppointmentType } from './FieldGrid';
import SegmentAudiencePreview, { isSegmentAudience } from './SegmentAudiencePreview';
import { executionFailureMessage } from './approvalAdapters';
import type { WriteProposal } from './types';

type Props = {
	proposal: WriteProposal;
	onUpdate?: (update: Partial<WriteProposal>) => void;
	/** Send a message on the user's behalf. When supplied, a failed card offers
	 *  "ask the assistant to fix it" — some failures name a problem only the
	 *  agent can act on, so re-running the identical payload cannot help. */
	onAskAgent?: (text: string) => void;
	defaultExpanded?: boolean;
	/** When true, the card renders in a fully inert state — no approve /
	 *  reject buttons, no field editors fire onChange. Used by the trace
	 *  drawer when displaying an already-resolved approval. */
	readOnly?: boolean;
};

const ENTITY_ICONS: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
	activity: CheckSquare,
	todo: CheckSquare,
	task: CheckSquare,
	appointment: Calendar,
	meeting: Calendar,
	order: Briefcase,
	deal: Briefcase,
	opportunity: Briefcase,
	client: Building2,
	account: Building2,
	company: Building2,
	contact: User,
	person: User,
	lead: Sparkles,
	agreement: FileText,
	ticket: Ticket,
	project: Folder,
	projectplan: ClipboardList,
	segment: Users
};

const ENTITY_LABEL_KEYS: Record<string, string> = {
	activity: 'default.activity',
	todo: 'todo.todo',
	task: 'default.task',
	appointment: 'default.appointment',
	meeting: 'assistant.meeting',
	order: 'default.order',
	deal: 'assistant.deal',
	opportunity: 'default.opportunity',
	client: 'default.client',
	account: 'default.account',
	company: 'default.account',
	contact: 'default.contact',
	person: 'default.contact',
	lead: 'default.lead',
	agreement: 'default.agreement',
	ticket: 'default.ticket',
	project: 'default.project',
	projectplan: 'default.projectPlan',
	segment: 'default.segment'
};

const ACTION_LABEL_KEY: Record<string, string> = {
	create: 'default.create',
	update: 'default.update',
	delete: 'default.delete'
};

const TITLE_KEYS = ['description', 'subject', 'name', 'title'];

// Chip values are raw field values. Collapse ISO date/datetime strings to just
// the calendar date (YYYY-MM-DD) so e.g. an appointment's date/endDate render as
// the day rather than the raw time + timezone offset.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/;
const chipValue = (v: string): string => (ISO_DATE.test(v) ? v.slice(0, 10) : v);

// Record ids reach the card as a number from the executor and as a string from
// the LLM's own payload. Non-numeric input yields NaN, which the callers' range
// check rejects.
const numericId = (v: unknown): number | null =>
	typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : null;

function buildSummary(proposal: WriteProposal): { title: string; chips: string[]; typePill: string | null } {
	const data = proposal.data ?? {};
	let title = '';
	for (const k of TITLE_KEYS) {
		const v = data[k];
		if (typeof v === 'string' && v.trim()) {
			title = v.trim();
			break;
		}
	}
	// For updates/deletes the LLM only sends changed fields + id, so the data
	// rarely carries a recognisable name. Fall back to the display name resolved
	// at draft time so the card reads "Uppdatera Företag · Acme Corp" instead of
	// just "Uppdatera Företag".
	if (!title && proposal.entityDisplayName) {
		title = proposal.entityDisplayName;
	}
	const rawActivityType = data?.activityType;
	const activityTypeName =
		typeof rawActivityType === 'string'
			? rawActivityType
			: typeof rawActivityType === 'object' &&
			  rawActivityType !== null &&
			  typeof (rawActivityType as { name?: unknown }).name === 'string'
			? (rawActivityType as { name: string }).name
			: undefined;
	const subtypeKey = crmEntityLabelKey(proposal.entity, {
		activityType: activityTypeName,
		probability: orderProbabilityOf(data)
	});
	const entityKey = subtypeKey ?? ENTITY_LABEL_KEYS[proposal.entity];
	const entityLabel = entityKey ? T(entityKey) : proposal.entity;
	const actionKey = ACTION_LABEL_KEY[proposal.action];
	const actionLabel = actionKey ? T(actionKey) : proposal.action;

	const chips: string[] = [];
	const typePill: string | null = null;
	if (data) {
		for (const [k, v] of Object.entries(data)) {
			if (TITLE_KEYS.includes(k) || k === 'id' || k === 'activityType') continue;
			if (chips.length >= 3) continue;
			if (typeof v === 'string' && v.trim()) chips.push(chipValue(v.trim()));
			else if (typeof v === 'number' || typeof v === 'boolean') chips.push(String(v));
		}
	}

	// If the record carries no date of its own, surface the approval's created date
	// (set only by the company-card section) so every card shows a date.
	if (proposal.createdDate && !chips.some(c => ISO_DATE.test(c))) chips.unshift(proposal.createdDate);

	const head = `${actionLabel} ${entityLabel}`;
	return { title: title ? `${head} · ${title}` : head, chips, typePill };
}

function statusToPill(status: WriteProposal['status'], action: WriteProposal['action']): StatusPill | null {
	switch (status) {
		case 'saving':
			return { label: T('assistant.saving') || 'Saving…', tone: 'saving' };
		case 'saved': {
			const key =
				action === 'delete'
					? 'assistant.actionDeleted'
					: action === 'create'
					? 'assistant.actionCreated'
					: 'assistant.actionUpdated';
			return { label: T(key) || 'Done', tone: 'ok' };
		}
		case 'cancelled':
			return { label: T('assistant.cancelled') || 'Cancelled', tone: 'muted' };
		case 'error':
			return { label: T('default.failed') || 'Failed', tone: 'error' };
		case 'pending':
		default:
			return null;
	}
}

const WriteCard = ({ proposal, onUpdate, onAskAgent, defaultExpanded, readOnly = false }: Props) => {
	/** Set only when the record saved in the real form but the approval row
	 *  could not be marked resolved. Kept out of `status` on purpose — see
	 *  `handleOpenForm`. */
	const [resolveWarning, setResolveWarning] = useState<string | null>(null);
	// A meeting proposal can arrive holding an activity type rather than an
	// appointment type — nothing on the write path checks — which renders as a
	// bare "#5" and saves a mistyped meeting. Substitute the customer's default
	// so the card shows a real type and approving writes a valid one. Only on a
	// live card: a resolved or read-only one must show what actually happened.
	const [editedData, setEditedData] = useState<Record<string, unknown>>(() =>
		readOnly || proposal.status !== 'pending'
			? { ...proposal.data }
			: withValidAppointmentType(proposal.entity, { ...proposal.data })
	);
	const Icon = ENTITY_ICONS[proposal.entity] ?? CheckSquare;

	const readOnlyKeys = useMemo(() => (proposal.action === 'update' ? new Set(['id']) : undefined), [proposal.action]);

	// Approve and retry both resolve 200 even when the executor itself failed
	// (status='rejected' with resultPayload.success=false). Surface that as a
	// Failed card with the executor's message instead of a misleading green
	// checkmark. Shared so the two paths cannot drift.
	const applyApprovalOutcome = useCallback(
		(approval: { resultPayload?: unknown; resultEntityType?: string | null; resultEntityId?: number | null }) => {
			if (!onUpdate) return;
			const failure = executionFailureMessage(
				approval.resultPayload as Record<string, unknown> | null | undefined
			);
			onUpdate({
				status: failure !== null ? 'error' : 'saved',
				data: editedData,
				error: failure !== null ? failure || T('default.failed') || 'Write failed' : undefined,
				result: (approval.resultPayload as WriteProposal['result']) ?? undefined,
				resultEntityType: approval.resultEntityType ?? null,
				resultEntityId: approval.resultEntityId ?? null
			});
		},
		[editedData, onUpdate]
	);

	/**
	 * "Edit in Upsales" — hand the proposal to the entity's own create form.
	 *
	 * The card can only show the fields the agent happened to send, so anything
	 * else (a meeting's agenda, custom fields, invited-contact toggles) is
	 * unreachable from here. This opens the real form instead, seeded with
	 * `editedData` so inline edits made on the card carry over rather than being
	 * silently dropped.
	 *
	 * The form saves the record itself, so the approval must be resolved against
	 * the id it reports rather than re-executed — otherwise the executor writes a
	 * second copy. Closing the form without saving leaves the proposal pending
	 * and the card untouched, which is what a cancel should do.
	 */
	const handleOpenForm = useCallback(async () => {
		if (!onUpdate) return;
		const saved = await openCrmEntityDraft(proposal.entity, editedData);
		if (!saved) return;

		if (!proposal.approvalId) {
			// A card with no approval row (direct executeWrite path) has nothing
			// to resolve — the record exists, so just reflect that.
			onUpdate({
				status: 'saved',
				data: editedData,
				result: saved as WriteProposal['result'],
				resultEntityType: proposal.entity,
				resultEntityId: saved.id
			});
			return;
		}

		onUpdate({ status: 'saving' });
		try {
			const { approval } = await AgentApprovalsResource.approve(
				proposal.approvalId,
				undefined,
				{ entity: proposal.entity, action: proposal.action, data: editedData },
				{ externalResult: { entityType: proposal.entity, entityId: saved.id } }
			);
			applyApprovalOutcome(approval);
		} catch (err) {
			// The record IS saved — only the bookkeeping failed. It must not go
			// to 'error': that state offers Retry, which re-executes the write
			// and would leave the customer with two copies of the same record.
			// Mark it saved (true — the record exists) and carry the warning
			// separately, so the card can say what happened without ever
			// becoming approvable again.
			setResolveWarning(T('assistant.openForm.saveFailed'));
			onUpdate({
				status: 'saved',
				data: editedData,
				resultEntityType: proposal.entity,
				resultEntityId: saved.id
			});
		}
	}, [proposal.entity, proposal.action, proposal.approvalId, editedData, onUpdate, applyApprovalOutcome]);

	const handleConfirm = useCallback(async () => {
		if (!onUpdate) return;
		onUpdate({ status: 'saving' });
		try {
			if (proposal.approvalId) {
				const modified = { entity: proposal.entity, action: proposal.action, data: editedData };
				const { approval } = await AgentApprovalsResource.approve(proposal.approvalId, undefined, modified);
				applyApprovalOutcome(approval);
			} else {
				const result = await AssistantResource.executeWrite(proposal.entity, proposal.action, editedData);
				const failure = executionFailureMessage(result as Record<string, unknown> | null | undefined);
				if (failure !== null) {
					onUpdate({
						status: 'error',
						data: editedData,
						error: failure || T('default.failed') || 'Write failed',
						result
					});
				} else {
					onUpdate({ status: 'saved', result, data: editedData });
				}
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Write failed';
			onUpdate({ status: 'error', error: message });
		}
	}, [proposal.entity, proposal.action, proposal.approvalId, editedData, onUpdate, applyApprovalOutcome]);

	const handleCancel = useCallback(async () => {
		if (!onUpdate) return;
		if (proposal.approvalId) {
			try {
				await AgentApprovalsResource.reject(proposal.approvalId, 'cancelled_in_chat');
			} catch {
				/* non-fatal */
			}
		}
		onUpdate({ status: 'cancelled' });
	}, [proposal.approvalId, onUpdate]);

	// A failed approval row is terminal server-side, so flipping the card back to
	// 'pending' locally just sends the next click into the resolve guard's
	// `approval_rejected`. The retry endpoint re-executes a resolved row, which is
	// what actually unsticks a card whose failure was transient or has since been
	// fixed by a deploy (WEB-9873).
	const handleRetry = useCallback(async () => {
		if (!onUpdate) return;
		if (!proposal.approvalId) {
			// No approval row: `handleConfirm` calls the write tool directly, so
			// re-arming the card IS the retry.
			onUpdate({ status: 'pending', error: undefined });
			return;
		}
		onUpdate({ status: 'saving', error: undefined });
		try {
			const { approval } = await AgentApprovalsResource.retry(proposal.approvalId);
			applyApprovalOutcome(approval);
		} catch (err) {
			onUpdate({ status: 'error', error: err instanceof Error ? err.message : 'Retry failed' });
		}
	}, [proposal.approvalId, onUpdate, applyApprovalOutcome]);

	// Hand the failure back to the agent. `reject` / `request-changes` both
	// require a pending row, so neither is available once the write has failed —
	// and the error text is often addressed to the agent, not the user.
	const handleAskAgent = useCallback(() => {
		if (!onAskAgent) return;
		const label = proposal.data?.description || proposal.data?.name || proposal.entity;
		onAskAgent(
			T('assistant.askAgentToFixWrite', {
				entity: proposal.entity,
				label: String(label),
				error: proposal.error ?? ''
			})
		);
	}, [onAskAgent, proposal.entity, proposal.data, proposal.error]);

	// Segment proposals carry the agent's friendly `audience` object — render it
	// as a purpose-built criteria preview instead of a generic JSON-ish badge,
	// and keep it out of the editable field grid (criteria aren't editable here;
	// the user can reject or request changes instead).
	const segmentAudience =
		proposal.entity === 'segment' && isSegmentAudience(editedData.audience) ? editedData.audience : null;
	const gridData = useMemo(
		() =>
			segmentAudience
				? Object.fromEntries(Object.entries(editedData).filter(([k]) => k !== 'audience'))
				: editedData,
		[segmentAudience, editedData]
	);
	// Edits in the grid (e.g. renaming the segment) must not drop the audience —
	// the executor needs it to build the filter blocks on confirm.
	const handleGridChange = useCallback(
		(next: Record<string, unknown>) =>
			setEditedData(segmentAudience ? { ...next, audience: segmentAudience } : next),
		[segmentAudience]
	);

	const summary = useMemo(() => {
		const base = buildSummary({ ...proposal, data: editedData });
		if (typeof proposal.matchingContacts === 'number') {
			base.chips = [
				`≈ ${proposal.matchingContacts.toLocaleString()} ${(
					T('default.contacts') || 'contacts'
				).toLowerCase()}`,
				...base.chips
			];
		}
		if (proposal.status === 'saved' && proposal.result?.id != null) {
			return {
				...base,
				title: `${base.title} · #${String(proposal.result.id)}`,
				chips: [`#${String(proposal.result.id)}`, ...base.chips]
			};
		}
		return base;
	}, [proposal, editedData]);
	const statusPill = statusToPill(proposal.status, proposal.action);
	const isSaving = proposal.status === 'saving';
	const resolved = proposal.status === 'saved' || proposal.status === 'cancelled';
	const errored = proposal.status === 'error';

	const resultEntityType = proposal.resultEntityType ?? proposal.entity;
	// An update/delete names its target in the payload, so the card knows the
	// record before the write as well as after it.
	const resultEntityId = proposal.resultEntityId ?? numericId(proposal.result?.id) ?? numericId(proposal.data?.id);
	const activityTypeName = useMemo(() => {
		const raw = editedData?.activityType;
		if (raw == null) return undefined;
		if (typeof raw === 'string') return raw;
		if (typeof raw === 'object' && raw && typeof (raw as { name?: unknown }).name === 'string') {
			return (raw as { name: string }).name;
		}
		return undefined;
	}, [editedData]);
	// Offer the link whenever a record exists to open: before the write for an
	// update or delete — the last moment the user can check what they are about
	// to change — and after it for a create. A create has nothing to open until
	// it saves (the "open in Upsales" draft action covers that), and a deleted
	// record is gone.
	const hasLiveRecord = proposal.status === 'saved' ? proposal.action !== 'delete' : proposal.action !== 'create';
	const showViewLink =
		hasLiveRecord &&
		!!resultEntityType &&
		typeof resultEntityId === 'number' &&
		Number.isFinite(resultEntityId) &&
		resultEntityId > 0 &&
		canOpenCrmEntity(resultEntityType);
	const handleOpenEntity = useCallback(() => {
		if (!showViewLink || !resultEntityType || resultEntityId == null) return;
		openCrmEntity(resultEntityType, resultEntityId, { activityType: activityTypeName });
	}, [showViewLink, resultEntityType, resultEntityId, activityTypeName]);
	const viewEntityLabel = useMemo(() => {
		if (!resultEntityType) return null;
		const key = crmEntityLabelKey(resultEntityType, {
			activityType: activityTypeName,
			probability: orderProbabilityOf(editedData)
		});
		return (key && T(key)) || resultEntityType;
	}, [resultEntityType, activityTypeName, editedData]);

	const body = (
		<div>
			{((errored && proposal.error) || resolveWarning) && (
				<div
					data-testid={resolveWarning ? 'proposal-resolve-warning' : undefined}
					style={{
						marginBottom: 10,
						padding: '8px 10px',
						borderRadius: 4,
						background: 'rgba(178,52,52,0.08)',
						color: 'var(--ws-err, #b23434)',
						fontSize: 12.5
					}}
				>
					{resolveWarning ?? proposal.error}
				</div>
			)}
			<FieldGrid
				data={gridData}
				onChange={handleGridChange}
				disabled={readOnly || isSaving || resolved}
				readOnlyKeys={readOnlyKeys}
				entity={proposal.entity}
			/>
			{segmentAudience && (
				<SegmentAudiencePreview audience={segmentAudience} matchingContacts={proposal.matchingContacts} />
			)}
		</div>
	);

	// Only on a live create: there is no "open the create form" for a record that
	// already exists (the header's Open link covers that), an update/delete has
	// no create form to prefill, and a resolved or errored card is past editing.
	const showOpenForm =
		!readOnly &&
		!resolved &&
		!errored &&
		!isSaving &&
		proposal.action === 'create' &&
		canEditCrmEntityDraft(proposal.entity);

	const headerAction =
		showViewLink && viewEntityLabel ? (
			<button
				type="button"
				className="ws-btn ws-btn-secondary ws-prop-mini-btn"
				data-testid="proposal-open-record"
				onClick={handleOpenEntity}
				title={T('default.entity.view', { entity: viewEntityLabel })}
			>
				<ExternalLink style={{ width: 12, height: 12 }} />
				<span>{T('default.open')}</span>
			</button>
		) : null;

	return (
		<ProposalCardShell
			icon={proposal.action === 'delete' ? Trash2 : Icon}
			title={summary.title}
			chips={summary.chips}
			typePill={summary.typePill}
			statusPill={statusPill}
			headerAction={headerAction}
			body={body}
			busy={isSaving}
			resolved={resolved}
			approveLabel={errored ? T('assistant.retry') : T(ACTION_LABEL_KEY[proposal.action] ?? 'default.save')}
			rejectLabel={T('assistant.cancel')}
			onApprove={readOnly ? undefined : errored ? handleRetry : resolved ? undefined : handleConfirm}
			onReject={readOnly || resolved || errored ? undefined : handleCancel}
			secondaryAction={
				// The two secondary actions are mutually exclusive by state:
				// "ask the assistant" only exists once a write has failed, and
				// "edit in Upsales" only while the proposal is still pending.
				!readOnly && errored && onAskAgent
					? {
							label: T('assistant.askAssistantToFix'),
							onClick: handleAskAgent,
							testId: 'proposal-ask-agent'
					  }
					: showOpenForm
					? {
							// Name the record, and say "edit" rather than "open": this
							// hands over the full record to work in, where saving
							// commits the write — unlike the header's Open, which
							// only shows it. Not "form"/"formulär" either; in Upsales
							// that word means a public lead-capture form.
							label: T('default.entity.edit', { entity: viewEntityLabel }),
							onClick: handleOpenForm,
							testId: 'proposal-open-form'
					  }
					: undefined
			}
			defaultExpanded={defaultExpanded}
		/>
	);
};

export default WriteCard;
